import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';

import { CountryDto, OneSkillRetakeRegistrationRequest, ProductDto, RegionDto, TestSessionForRegistrationDto, UserAccountRegDto } from 'src/app/core/dtos/dto';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { RegistrationManagementService } from 'src/app/core/services/registration-management.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { BrandEnum, RegStatusEnum, TestTypeEnum } from 'src/app/core/dtos/enum';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-order-one-skill-retake',
  templateUrl: './order-one-skill-retake.component.html',
  styleUrls: ['./order-one-skill-retake.component.scss']
})
export class OrderOneSkillRetakeComponent implements OnInit {

  @ViewChild('errorModal') errorModal: ModalComponent;
  brandId: BrandEnum;
  regId: number;
  oneSkillRetakeProductsForm: FormGroup;
  testInfo: UserAccountRegDto;
  components: ProductDto[];
  currencyCode: string;
  selectedProductId: number;
  modalBody: string;
  countries: CountryDto[];
  regions: RegionDto[];
  showRegion: boolean;
  regionId: number;
  countryId: number;
  city: string;
  cities: string[];
  sittingId: number;
  sittings: TestSessionForRegistrationDto[];
  publicSittings: TestSessionForRegistrationDto[];
  showAlert: boolean;
  isValidReg: boolean;
  identityDocumentCheck: boolean;

  constructor(
    private configHelper: ConfigHelper,
    private activatedRoute: ActivatedRoute,
    private registrationService: RegistrationService,
    private registrationManagementService: RegistrationManagementService,
    private countryRegionService: CountryRegionService,
    private router: Router,
    private loadingService: LoadingService,
    private fb: FormBuilder,
    private toastService: HotToastService
  ) {
    this.regId = Number(this.activatedRoute.snapshot.params.regId);
  }

  ngOnInit(): void {
    this.brandId = this.configHelper.getBrandId();
    this.oneSkillRetakeProductsForm = this.fb.group({
      selectedProducts: this.fb.array([])
    });
    this.getRegistrationDetails();
    this.getOneSkillRetakeProducts();
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  public get TestTypeEnum() {
    return TestTypeEnum;
  }

  getRegistrationDetails() {
    this.registrationService.getTestInfo(this.regId)
      .subscribe(
        (result) => {
          this.testInfo = result.content;
          if (result.content.isCancelledOrRefunded || result.content.regStatusEnum !== RegStatusEnum.ScorePublished) {
            this.isValidReg = false;
            this.modalBody = "Unable to proceed. Invalid registration status.";
            this.errorModal.open();
          }
          else if (!result.content.canOrderOneSkillRetake) {
            this.isValidReg = false;
            this.modalBody = "Unable to proceed. Your test date is older than 60 days.";
            this.errorModal.open();
          }
          else {
            this.isValidReg = true;
            this.getCountriesAndRegions();
          }
          this.loadingService.hide();
        },
        () => {
          this.loadingService.hide();
          this.toastService.warning("Unable to process your request");
        }
      );
  }

  getOneSkillRetakeProducts() {
    this.loadingService.show();
    this.registrationManagementService.getOneSkillRetakeProducts(this.regId)
      .subscribe(
        (result) => {
          this.components = result.content;
          if (result.content.length > 0) {
            this.currencyCode = this.components[0].currencyCode;
          }
          this.loadingService.hide();
        },
        () => {
          this.loadingService.hide();
          this.toastService.warning("Unable to process your request");
        }
      );
  }

  onSelect(event) {
    this.selectedProductId = Number(event.target.id);
  }

  getCountriesAndRegions() {
    this.countryId = this.testInfo.countryId;
    this.regionId = null;
    this.cities = null;
    this.showRegion = false;
    this.showAlert = false;
    forkJoin([this.countryRegionService.getCountriesWithActiveTestCentres(), this.countryRegionService.getRegionsWithCertainTestType(this.countryId, this.testInfo.testTypeId)])
      .subscribe(
        (result) => {
          this.countries = result[0].content.filter(c => c.id == this.countryId);
          this.regions = result[1].content;
          if (this.regions.length > 0) {
            this.showRegion = true;
          }
          else {
            this.showAlert = true;
          }
        },
        () => {
          this.toastService.warning("Unable to load regions");
        });
  }

  getTestCentreSittings() {
    this.city = null;
    this.sittingId = null;
    this.getTestSessionsForRegistration(this.testInfo.testPurposeId, this.testInfo.testTypeId, this.regionId);
  }

  getTestSessionsForRegistration(testPurposeId: number, testTypeId: number, regionId: number) {
    this.showAlert = false;
    this.registrationService.getTestSessionsForRegistration(this.brandId, testPurposeId, testTypeId, regionId, this.testInfo.regId)
      .subscribe(
        result => {
          this.loadingService.hide();
          this.sittings = result.content;
          this.publicSittings = result.content;
          if (this.sittings.length == 0) {
            this.showAlert = true;
          }
          this.cities = Array.from(new Set(this.sittings.map(({ testCentreCity }) => testCentreCity)));
        },
        () => {
          this.loadingService.hide();
          this.toastService.warning("Unable to load sittings.");
        });
  }

  filterSittingsByCity() {
    this.publicSittings = this.sittings.filter(x => x.testCentreCity === this.city);
  }

  redirectToMyAccount() {
    this.router.navigate(['my-account']);
  }

  addOSRToCart() {
    const OSRRegistrationRequest: OneSkillRetakeRegistrationRequest = {
      SourceRegId: this.testInfo.regId,
      RetakeProductId: this.selectedProductId,
      TargetTestSessionId: this.sittingId
    };
    this.registrationService.postOSRRegShoppingCart(OSRRegistrationRequest).subscribe(
      () => {
        this.router.navigate(['shopping-cart']);
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
        else {
          this.toastService.warning("Unable to add registration to shopping cart");
        }
      });
  }

}


